/**
 * ReceiptLightbox — reusable zoomable / pannable multi-image receipt viewer.
 * Fetches all uploaded files for a record and shows a main viewer (zoom + drag
 * to pan, mouse-wheel zoom) with a thumbnail strip + prev/next navigation.
 * Used as the MIDDLE column of the audit 3-way compare modal, but reusable
 * anywhere a record's proof photos must be inspected closely.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../contexts/AuthContext';
import {
  ImageIcon, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw, FileText, Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReceiptLightbox({ recordType, recordId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  const load = useCallback(async () => {
    if (!recordType || !recordId) return;
    setLoading(true);
    try {
      const res = await api.get(`${BACKEND_URL}/api/uploads/record/${recordType}/${recordId}`);
      const all = (res.data.sessions || []).flatMap(s => s.files || []);
      setFiles(all);
      setIdx(0);
    } catch { /* empty */ }
    setLoading(false);
  }, [recordType, recordId]);

  useEffect(() => { load(); }, [load]);

  // reset zoom/pan whenever the active image changes
  useEffect(() => { setScale(1); setOffset({ x: 0, y: 0 }); }, [idx]);

  const fileUrl = (f) => `${BACKEND_URL}/api/uploads/file/${recordType}/${recordId}/${f.id}`;
  const isPdf = (f) => f?.content_type === 'application/pdf' || f?.filename?.endsWith('.pdf');

  const current = files[idx];
  const zoomIn = () => setScale(s => Math.min(5, +(s + 0.5).toFixed(2)));
  const zoomOut = () => setScale(s => Math.max(1, +(s - 0.5).toFixed(2)));
  const resetZoom = () => { setScale(1); setOffset({ x: 0, y: 0 }); };

  const onWheel = (e) => {
    e.preventDefault();
    setScale(s => Math.min(5, Math.max(1, +(s - e.deltaY * 0.002).toFixed(2))));
  };
  const onMouseDown = (e) => {
    if (scale <= 1) return;
    dragRef.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    setOffset({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y });
  };
  const endDrag = () => { dragRef.current = null; };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12 text-slate-400" data-testid="compare-photos-loading">
        <Loader2 size={20} className="animate-spin mb-2" />
        <p className="text-xs">Loading receipts…</p>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12 text-center text-slate-400" data-testid="compare-photos-empty">
        <ImageIcon size={28} className="mb-2 opacity-40" />
        <p className="text-sm font-medium text-slate-500">No receipt photo attached</p>
        <p className="text-xs mt-1 max-w-[200px]">Use “View on Phone” to snap &amp; attach the supplier receipt, then refresh.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="compare-photos">
      {/* Main viewer */}
      <div className="relative flex-1 min-h-[260px] bg-slate-900 rounded-lg overflow-hidden flex items-center justify-center select-none"
        onWheel={onWheel} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={endDrag} onMouseLeave={endDrag}
        style={{ cursor: scale > 1 ? 'grab' : 'default' }}>
        {isPdf(current) ? (
          <div className="flex flex-col items-center justify-center text-white p-8">
            <FileText size={40} className="text-red-400 mb-3" />
            <p className="text-xs">{current.filename}</p>
            <a href={fileUrl(current)} target="_blank" rel="noreferrer" className="mt-3 text-xs underline text-blue-300">Open PDF</a>
          </div>
        ) : (
          <img src={fileUrl(current)} alt={current.filename} draggable={false}
            className="max-h-full max-w-full object-contain transition-transform duration-75"
            style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }} />
        )}

        {/* Nav arrows */}
        {files.length > 1 && (
          <>
            <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0} data-testid="compare-photo-prev"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 disabled:opacity-30">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setIdx(i => Math.min(files.length - 1, i + 1))} disabled={idx === files.length - 1} data-testid="compare-photo-next"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 disabled:opacity-30">
              <ChevronRight size={16} />
            </button>
          </>
        )}

        {/* Zoom controls */}
        {!isPdf(current) && (
          <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/55 rounded-lg p-1">
            <button onClick={zoomOut} data-testid="compare-zoom-out" className="w-7 h-7 rounded-md text-white hover:bg-white/15 flex items-center justify-center"><ZoomOut size={14} /></button>
            <span className="text-[10px] text-white font-mono w-9 text-center">{Math.round(scale * 100)}%</span>
            <button onClick={zoomIn} data-testid="compare-zoom-in" className="w-7 h-7 rounded-md text-white hover:bg-white/15 flex items-center justify-center"><ZoomIn size={14} /></button>
            <button onClick={resetZoom} data-testid="compare-zoom-reset" className="w-7 h-7 rounded-md text-white hover:bg-white/15 flex items-center justify-center"><RotateCcw size={13} /></button>
          </div>
        )}

        <div className="absolute top-2 left-2 bg-black/55 text-white text-[10px] px-2 py-0.5 rounded-full">{idx + 1} / {files.length}</div>
      </div>

      {/* Thumbnail strip */}
      {files.length > 1 && (
        <div className="flex gap-1.5 mt-2 overflow-x-auto pb-1">
          {files.map((f, i) => (
            <button key={f.id} onClick={() => setIdx(i)} data-testid={`compare-thumb-${i}`}
              className={`shrink-0 w-12 h-12 rounded-md overflow-hidden border-2 transition-colors ${i === idx ? 'border-[#1A4D2E]' : 'border-transparent opacity-70 hover:opacity-100'}`}>
              {isPdf(f) ? (
                <div className="w-full h-full bg-slate-100 flex items-center justify-center"><FileText size={16} className="text-red-500" /></div>
              ) : (
                <img src={fileUrl(f)} alt={f.filename} className="w-full h-full object-cover" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
